import React, { useState, useEffect, useRef } from 'react';
import { 
  Edit2, Save, X, RefreshCw, Printer,
  Building2, Calendar, DollarSign, Plus, Trash2 
} from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const GeneralSetup = () => {
  // State Management
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Company Data
  const [companyData, setCompanyData] = useState({
    companyid: null,
    companycode: '',
    companyname: '',
    companynamearabic: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    phone: '',
    fax: '',
    email: '',
    website: '',
    taxnumber: '',
    registrationnumber: '',
    logopath: '',
    isactive: 1,
    IsDefault: 0
  });
  
  // Original data for cancel operation
  const [originalData, setOriginalData] = useState(null);
  
  // Currencies
  const [currencies, setCurrencies] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState(null);
  
  // Financial Year Info
  const [financialYearInfo, setFinancialYearInfo] = useState('Loading financial year information...');
  
  // Exchange Rates
  const [exchangeRates, setExchangeRates] = useState([]);
  const [originalExchangeRates, setOriginalExchangeRates] = useState([]);
  
  // Errors
  const [errors, setErrors] = useState({});
  
  // Refs
  const companyNameRef = useRef(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await loadCurrencies();
      await loadCompanyData();
      await loadFinancialYear();
      // Exchange rates loaded after currencies
      updateStatus('General Setup loaded successfully');
    } catch (error) {
      console.error('Error loading data:', error);
      updateStatus('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrencies = async () => {
    try {
      const response = await apiClient.get('/Currencies.php', {
        params: { active: true }
      });
      if (response.data.success) {
        const currencyList = response.data.data || [];
        setCurrencies(currencyList);
        
        // Find base currency (statusbasecurrency = 1)
        const base = currencyList.find(c => c.statusbasecurrency === 1);
        if (base) {
          setBaseCurrency(base);
          // Load exchange rates for base currency
          loadExchangeRates(base.currencyid);
        }
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
      alert('Error loading currencies. Please check your Currencies table.');
    }
  };

  const loadCompanyData = async () => {
    try {
      // Get current company ID from session
      const companyId = sessionStorage.getItem('companyid') || 1;
      
      const response = await apiClient.get('/Companies.php', {
        params: { id: companyId }
      });
      
      if (response.data.success && response.data.data) {
        const company = response.data.data;
        setCompanyData({
          companyid: company.companyid,
          companycode: company.companycode || '',
          companyname: company.companyname || '',
          companynamearabic: company.companynamearabic || '',
          address: company.address || '',
          city: company.city || '',
          state: company.state || '',
          country: company.country || '',
          postal_code: company.postal_code || '',
          phone: company.phone || '',
          fax: company.fax || '',
          email: company.email || '',
          website: company.website || '',
          taxnumber: company.taxnumber || '',
          registrationnumber: company.registrationnumber || '',
          logopath: company.logopath || '',
          isactive: company.isactive || 1,
          IsDefault: company.IsDefault || 0
        });
        setOriginalData({...company});
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const loadFinancialYear = async () => {
    try {
      const companyId = sessionStorage.getItem('companyid') || 1;
      
      // Get current financial year
      const response = await apiClient.get('/FinancialYears.php', {
        params: { 
          companyid: companyId,
          current: true
        }
      });
      
      if (response.data.success && response.data.data) {
        const fyData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
        if (fyData) {
          const fromDate = new Date(fyData.startdate).toLocaleDateString('en-GB');
          const toDate = new Date(fyData.enddate).toLocaleDateString('en-GB');
          const status = fyData.isclosed ? 'Closed' : 'Open';
          setFinancialYearInfo(
            `Current Financial Year: ${fyData.yearname} (${fromDate} to ${toDate}) - Status: ${status}`
          );
        }
      } else {
        setFinancialYearInfo('No current financial year information available');
      }
    } catch (error) {
      console.error('Error loading financial year:', error);
      setFinancialYearInfo('Error loading financial year information');
    }
  };

  const loadExchangeRates = async (baseCurrencyId) => {
    try {
      if (!baseCurrencyId) {
        setExchangeRates([]);
        setOriginalExchangeRates([]);
        return;
      }

      const response = await apiClient.get('/ExchangeRates.php', {
        params: { 
          tocurrencyid: baseCurrencyId
        }
      });
      
      if (response.data.success) {
        const rates = response.data.data || [];
        setExchangeRates(rates);
        setOriginalExchangeRates(JSON.parse(JSON.stringify(rates)));
      }
    } catch (error) {
      console.error('Error loading exchange rates:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setCompanyData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const addExchangeRateRow = () => {
    if (!baseCurrency) {
      alert('Please set a base currency first in the Currencies master.');
      return;
    }
    
    const newRate = {
      exchangerateid: null,
      fromcurrencyid: '',
      rate: '',
      effectivedate: new Date().toISOString().split('T')[0],
      tocurrencyid: baseCurrency.currencyid,
      isNew: true
    };
    setExchangeRates(prev => [...prev, newRate]);
  };

  const removeExchangeRateRow = (index) => {
    if (window.confirm('Remove this exchange rate?')) {
      setExchangeRates(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateExchangeRate = (index, field, value) => {
    setExchangeRates(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  const validateInput = () => {
    const newErrors = {};
    
    if (!companyData.companyname?.trim()) {
      newErrors.companyname = 'Company name is required';
    }
    
    if (!companyData.companycode?.trim()) {
      newErrors.companycode = 'Company code is required';
    }
    
    if (companyData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    // Validate exchange rates
    exchangeRates.forEach((rate, index) => {
      if (!rate.fromcurrencyid) {
        newErrors[`rate_currency_${index}`] = 'Currency is required';
      }
      if (!rate.rate || parseFloat(rate.rate) <= 0) {
        newErrors[`rate_value_${index}`] = 'Valid rate is required';
      }
      if (!rate.effectivedate) {
        newErrors[`rate_date_${index}`] = 'Date is required';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = () => {
    setEditMode(true);
    setOriginalData(JSON.parse(JSON.stringify(companyData)));
    setOriginalExchangeRates(JSON.parse(JSON.stringify(exchangeRates)));
    updateStatus('Edit mode - Modify company details and exchange rates');
    
    setTimeout(() => {
      if (companyNameRef.current) {
        companyNameRef.current.focus();
      }
    }, 100);
  };

  const handleSave = async () => {
    if (!validateInput()) {
      updateStatus('Save cancelled - validation failed');
      return;
    }
    
    setSubmitLoading(true);
    try {
      updateStatus('Saving company details...');
      
      // Save company data
      const companyResponse = await apiClient.put('/Companies.php', companyData);
      
      if (!companyResponse.data.success) {
        throw new Error(companyResponse.data.message || 'Failed to save company data');
      }
      
      if (baseCurrency) {
        updateStatus('Saving exchange rates...');
        
        // Save exchange rates
        for (const rate of exchangeRates) {
          if (rate.isNew || !rate.exchangerateid) {
            // Create new rate
            await apiClient.post('/ExchangeRates.php', {
              fromcurrencyid: rate.fromcurrencyid,
              tocurrencyid: baseCurrency.currencyid,
              rate: parseFloat(rate.rate),
              effectivedate: rate.effectivedate
            });
          } else {
            // Update existing rate
            await apiClient.put('/ExchangeRates.php', {
              exchangerateid: rate.exchangerateid,
              fromcurrencyid: rate.fromcurrencyid,
              tocurrencyid: baseCurrency.currencyid,
              rate: parseFloat(rate.rate),
              effectivedate: rate.effectivedate
            });
          }
        }
        
        // Check for deleted rates
        const deletedRates = originalExchangeRates.filter(
          orig => !exchangeRates.find(curr => curr.exchangerateid === orig.exchangerateid)
        );
        
        for (const rate of deletedRates) {
          if (rate.exchangerateid) {
            await apiClient.delete('/ExchangeRates.php', {
              data: { exchangerateid: rate.exchangerateid }
            });
          }
        }
      }
      
      alert('Company setup saved successfully!');
      setEditMode(false);
      loadAllData();
      updateStatus('Save completed successfully');
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving data: ' + (error.response?.data?.message || error.message));
      updateStatus('Save error: ' + error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Discard all changes?')) {
      setCompanyData(originalData);
      setExchangeRates(originalExchangeRates);
      setEditMode(false);
      setErrors({});
      updateStatus('Changes cancelled - Data restored');
    }
  };

  const handleRefresh = async () => {
    await loadAllData();
    alert('Data refreshed successfully!');
  };

  const handlePrint = () => {
    window.print();
  };

  const updateStatus = (message) => {
    console.log(`GeneralSetup: ${message} - ${new Date().toLocaleTimeString()}`);
  };

  const getCurrencyName = (currencyId) => {
    const currency = currencies.find(c => c.currencyid === parseInt(currencyId));
    return currency ? currency.currencyname : '';
  };

  if (loading) {
    return (
      <div className="master-container">
        <div className="loading-state">Loading General Setup...</div>
      </div>
    );
  }

  return (
    <div className="master-container">
      {/* Toolbar */}
      <div className="master-header" style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '16px' }}>
        <div className="header-left">
          <h2>
            <Building2 size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            General Setup - Company Configuration
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!editMode ? (
            <>
              <button className="btn-primary" onClick={handleEdit} title="Edit (F3)">
                <Edit2 size={18} />
                Edit
              </button>
              <button className="btn-secondary" onClick={handleRefresh} title="Refresh (F6)">
                <RefreshCw size={18} />
                Refresh
              </button>
              <button className="btn-secondary" onClick={handlePrint} title="Print (F7)">
                <Printer size={18} />
                Print
              </button>
            </>
          ) : (
            <>
              <button 
                className="btn-primary" 
                onClick={handleSave} 
                disabled={submitLoading}
                title="Save (F5)"
              >
                <Save size={18} />
                {submitLoading ? 'Saving...' : 'Save'}
              </button>
              <button 
                className="btn-secondary" 
                onClick={handleCancel}
                disabled={submitLoading}
                title="Cancel (Esc)"
              >
                <X size={18} />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Company Details Section */}
      <div className="form-section" style={{ marginTop: '20px' }}>
        <div className="section-header">
          <Building2 size={20} />
          <h3>Company Details</h3>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="companycode">
              Company Code <span className="required">*</span>
            </label>
            <input
              type="text"
              id="companycode"
              value={companyData.companycode}
              onChange={(e) => handleInputChange('companycode', e.target.value)}
              readOnly={!editMode}
              className={errors.companycode ? 'error' : ''}
              maxLength="20"
            />
            {errors.companycode && (
              <span className="error-message">{errors.companycode}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="companyname">
              Company Name <span className="required">*</span>
            </label>
            <input
              ref={companyNameRef}
              type="text"
              id="companyname"
              value={companyData.companyname}
              onChange={(e) => handleInputChange('companyname', e.target.value)}
              readOnly={!editMode}
              className={errors.companyname ? 'error' : ''}
              maxLength="200"
            />
            {errors.companyname && (
              <span className="error-message">{errors.companyname}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="companynamearabic">Company Name (Arabic)</label>
            <input
              type="text"
              id="companynamearabic"
              value={companyData.companynamearabic}
              onChange={(e) => handleInputChange('companynamearabic', e.target.value)}
              readOnly={!editMode}
              maxLength="200"
              dir="rtl"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              value={companyData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              readOnly={!editMode}
              maxLength="500"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              value={companyData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              readOnly={!editMode}
              maxLength="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="state">State</label>
            <input
              type="text"
              id="state"
              value={companyData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              readOnly={!editMode}
              maxLength="100"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="country">Country</label>
            <input
              type="text"
              id="country"
              value={companyData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              readOnly={!editMode}
              maxLength="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="postal_code">Postal Code</label>
            <input
              type="text"
              id="postal_code"
              value={companyData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              readOnly={!editMode}
              maxLength="20"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              type="text"
              id="phone"
              value={companyData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              readOnly={!editMode}
              maxLength="50"
            />
          </div>

          <div className="form-group">
            <label htmlFor="fax">Fax</label>
            <input
              type="text"
              id="fax"
              value={companyData.fax}
              onChange={(e) => handleInputChange('fax', e.target.value)}
              readOnly={!editMode}
              maxLength="50"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={companyData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              readOnly={!editMode}
              maxLength="100"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              value={companyData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              readOnly={!editMode}
              maxLength="200"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="taxnumber">Tax Number</label>
            <input
              type="text"
              id="taxnumber"
              value={companyData.taxnumber}
              onChange={(e) => handleInputChange('taxnumber', e.target.value)}
              readOnly={!editMode}
              maxLength="50"
            />
          </div>

          <div className="form-group">
            <label htmlFor="registrationnumber">Registration Number</label>
            <input
              type="text"
              id="registrationnumber"
              value={companyData.registrationnumber}
              onChange={(e) => handleInputChange('registrationnumber', e.target.value)}
              readOnly={!editMode}
              maxLength="50"
            />
          </div>
        </div>
      </div>

      {/* Base Currency Info */}
      {baseCurrency && (
        <div className="form-section" style={{ marginTop: '20px' }}>
          <div className="section-header">
            <DollarSign size={20} />
            <h3>Base Currency</h3>
          </div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f0fdf4', 
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#166534',
            fontSize: '15px',
            fontWeight: '500'
          }}>
            {baseCurrency.currencyname} ({baseCurrency.symbol}) - Set in Currencies Master
          </div>
        </div>
      )}

      {/* Financial Year Section */}
      <div className="form-section" style={{ marginTop: '20px' }}>
        <div className="section-header">
          <Calendar size={20} />
          <h3>Current Financial Year Information</h3>
        </div>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#eff6ff', 
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          color: '#1e40af',
          fontSize: '15px',
          fontWeight: '500'
        }}>
          {financialYearInfo}
        </div>
      </div>

      {/* Exchange Rates Section */}
      <div className="form-section" style={{ marginTop: '20px' }}>
        <div className="section-header">
          <DollarSign size={20} />
          <h3>Exchange Rates (Against Base Currency)</h3>
          {editMode && baseCurrency && (
            <button 
              className="btn-primary" 
              onClick={addExchangeRateRow}
              style={{ marginLeft: 'auto' }}
            >
              <Plus size={16} />
              Add Rate
            </button>
          )}
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Exchange Rate</th>
                <th>Effective Date</th>
                {editMode && <th className="actions-column">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {exchangeRates.length === 0 ? (
                <tr>
                  <td colSpan={editMode ? "4" : "3"} style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    {baseCurrency 
                      ? 'No exchange rates defined. Click "Add Rate" to add exchange rates.' 
                      : 'Please set a base currency in the Currencies master first.'}
                  </td>
                </tr>
              ) : (
                exchangeRates.map((rate, index) => (
                  <tr key={index}>
                    <td>
                      {editMode ? (
                        <select
                          value={rate.fromcurrencyid}
                          onChange={(e) => updateExchangeRate(index, 'fromcurrencyid', e.target.value)}
                          className={errors[`rate_currency_${index}`] ? 'error' : ''}
                          style={{ width: '100%' }}
                        >
                          <option value="">-- Select Currency --</option>
                          {currencies
                            .filter(c => c.currencyid !== baseCurrency?.currencyid && c.isactive === 1)
                            .map(currency => (
                              <option key={currency.currencyid} value={currency.currencyid}>
                                {currency.currencyname} ({currency.symbol})
                              </option>
                            ))}
                        </select>
                      ) : (
                        <span className="country-badge">
                          {getCurrencyName(rate.fromcurrencyid)}
                        </span>
                      )}
                      {errors[`rate_currency_${index}`] && (
                        <span className="error-message">{errors[`rate_currency_${index}`]}</span>
                      )}
                    </td>
                    <td>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.000001"
                          value={rate.rate}
                          onChange={(e) => updateExchangeRate(index, 'rate', e.target.value)}
                          className={errors[`rate_value_${index}`] ? 'error' : ''}
                          style={{ 
                            width: '100%', 
                            backgroundColor: '#fef3c7',
                            textAlign: 'right'
                          }}
                        />
                      ) : (
                        <span style={{ fontWeight: '600' }}>
                          {parseFloat(rate.rate).toFixed(6)}
                        </span>
                      )}
                      {errors[`rate_value_${index}`] && (
                        <span className="error-message">{errors[`rate_value_${index}`]}</span>
                      )}
                    </td>
                    <td>
                      {editMode ? (
                        <input
                          type="date"
                          value={rate.effectivedate}
                          onChange={(e) => updateExchangeRate(index, 'effectivedate', e.target.value)}
                          className={errors[`rate_date_${index}`] ? 'error' : ''}
                          style={{ 
                            width: '100%',
                            backgroundColor: '#fef3c7'
                          }}
                        />
                      ) : (
                        new Date(rate.effectivedate).toLocaleDateString('en-GB')
                      )}
                      {errors[`rate_date_${index}`] && (
                        <span className="error-message">{errors[`rate_date_${index}`]}</span>
                      )}
                    </td>
                    {editMode && (
                      <td className="actions-column">
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => removeExchangeRateRow(index)}
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Print-only section */}
      <div className="print-only" style={{ display: 'none' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '30px' }}>COMPANY SETUP</h1>
        <p><strong>Company:</strong> {companyData.companyname}</p>
        <p><strong>Address:</strong> {companyData.address}</p>
        <p><strong>City:</strong> {companyData.city}, {companyData.state} {companyData.postal_code}</p>
        <p><strong>Country:</strong> {companyData.country}</p>
        <p><strong>Phone:</strong> {companyData.phone}</p>
        <p><strong>Email:</strong> {companyData.email}</p>
        <p><strong>Tax No:</strong> {companyData.taxnumber}</p>
        <p><strong>Registration No:</strong> {companyData.registrationnumber}</p>
        {baseCurrency && <p><strong>Base Currency:</strong> {baseCurrency.currencyname} ({baseCurrency.symbol})</p>}
      </div>

      <style jsx>{`
        @media print {
          .master-header,
          .section-header button,
          .actions-column,
          button {
            display: none !important;
          }
          
          .print-only {
            display: block !important;
          }
          
          input, select {
            border: none !important;
            background: transparent !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GeneralSetup;